import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  routeDetailsClasses,
  skeletonClasses,
} from 'styles/tailwindClassnames';

const GENERAL_TAB_SKELETON_ROWS = [
  { label: 'w-32', desc: 'w-52' },
  { label: 'w-24', desc: 'w-44' },
];

const skeletonBar = (w: string, h: string = 'h-4') => (
  <div className={`${h} ${w} ${skeletonClasses.base}`} />
);

// The Route page while the Route loads: its header, tab strip and the open
// tab's layout.
const RouteDetailsSkeleton = ({ subpage }: { subpage: string }) => {
  const { t } = useTranslation();
  return (
    /* Same tint and bleed as the loaded page below, so the skeleton is not a
         white screen that turns teal the moment the data lands. */
    <div
      className="relative -mx-4 -mb-6 h-full min-h-[calc(100vh-80px)] bg-teal-t10 px-4 pb-6 md:-mx-8 md:min-h-[calc(100vh-70px)] md:px-8 desktop:-mx-[60px] desktop:px-[60px]"
      role="status"
      aria-busy="true"
    >
      <span className="sr-only">{t('Loading…')}</span>
      {/* Header skeleton */}
      <div className={routeDetailsClasses.header}>
        <div className="mx-auto max-w-list-table">
          <div className="mb-3 flex flex-row justify-between">
            <div className="flex w-3/4 flex-col gap-2">
              {skeletonBar('w-64', 'h-8')}
              <div className="mt-1 flex flex-row gap-2">
                {skeletonBar('w-24')}
                {skeletonBar('w-32')}
              </div>
            </div>
            {skeletonBar('w-40', 'h-8')}
          </div>
          {/* Tabs skeleton — General, Provider, Destinations, Sources, Logs */}
          <div className="flex flex-row gap-6 pt-1">
            {['w-16', 'w-16', 'w-24', 'w-16', 'w-10'].map((w, i) => (
              <div
                key={i}
                className={`${w} h-4 ${skeletonClasses.base} mb-3`}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Content skeleton — varies by subpage */}
      {subpage === 'configurations' ? (
        <div className="mx-auto mt-8 max-w-list-table px-0 md:px-4">
          {/* Flow map card */}
          <div className="mb-6 rounded-lg border border-teal-t30 bg-white">
            <div className="flex items-center gap-3 border-b border-ui-divider-lines px-5 py-3">
              {skeletonBar('w-28', 'h-5')}
              {skeletonBar('w-40', 'h-9 ml-auto')}
            </div>
            <div className="flex flex-col gap-6 px-3 py-8 md:flex-row md:items-center md:gap-16 md:px-5">
              {skeletonBar('w-full md:w-[320px]', 'h-[116px]')}
              <div className="flex w-full flex-col gap-4 md:w-auto">
                {skeletonBar('w-full md:w-[320px]', 'h-[116px]')}
                {skeletonBar('w-full md:w-[320px]', 'h-[116px]')}
              </div>
            </div>
          </div>
          {/* Selection panel */}
          <div className="rounded-lg border border-teal-t30 bg-white p-5">
            {['w-48', 'w-64', 'w-56'].map((w, i) => (
              <div key={i} className="mb-3">
                {skeletonBar(w)}
              </div>
            ))}
          </div>
        </div>
      ) : subpage === 'sources' ? (
        <div className="mx-auto mt-8 max-w-list-table">
          {skeletonBar('w-96 max-w-full', 'h-4 mb-6')}
          {/* Filter bar */}
          <div className="mb-3 flex flex-row flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              {skeletonBar('w-12', 'h-3 mb-1')}
              {skeletonBar('w-48', 'h-9')}
            </div>
            <div className="flex flex-col gap-1">
              {skeletonBar('w-20', 'h-3 mb-1')}
              {skeletonBar('w-48', 'h-9')}
            </div>
          </div>
          {skeletonBar('w-32', 'h-4 mb-3')}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-greyscale-9">
                  {['w-40', 'w-48', 'w-32'].map((w, i) => (
                    <th key={i} className="pb-3 pr-6 text-left">
                      <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-greyscale-9">
                    {['w-44', 'w-52', 'w-36'].map((w, j) => (
                      <td key={j} className="py-3 pr-6">
                        <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="mx-auto mt-8 w-full max-w-list-table">
          <div className="flex w-full flex-col px-0 md:px-4">
            {/* A white card, like the panel this stands in for. */}
            <div className="rounded-lg border border-teal-t30 bg-white p-[16px]">
              {GENERAL_TAB_SKELETON_ROWS.map((row, i) => (
                <div
                  key={i}
                  className="mb-[8px] border-b border-ui-divider-lines pb-[8px] pt-1 last:mb-0 last:border-b-0 last:pb-0"
                >
                  <div className="flex w-full flex-row items-start justify-between gap-6">
                    <div className="flex w-1/2 flex-col gap-2">
                      {skeletonBar(row.label)}
                      {skeletonBar(row.desc, 'h-3')}
                    </div>
                    <div className="w-1/2 p-2">
                      {skeletonBar('w-full', 'h-9')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetailsSkeleton;
